import { ICommonSettings, ILanguageModel, INotificationPermissionLabelSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { ReactNode } from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigNotificationPermissionLabelsSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingNotificationPermissionLabelsSetting?: INotificationPermissionLabelSetting;
  notificationPermissionLabels?: {
    languageName: string;
    cultureName: string;
    value: string;
  }[];
}

export interface IConfigNotificationPermissionLabelsSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingNotificationPermissionLabelsId?: string;
  languages: ILanguageModel[];
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigNotificationPermissionLabelsSettings extends React.PureComponent<
  IConfigNotificationPermissionLabelsSettingsProps,
  IConfigNotificationPermissionLabelsSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigNotificationPermissionLabelsSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingNotificationPermissionLabelsSetting: undefined,
      notificationPermissionLabels: []
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigNotificationPermissionLabelsSettingsProps) {
    if (prevProps.editingNotificationPermissionLabelsId && !this.props.editingNotificationPermissionLabelsId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingNotificationPermissionLabelsId !== this.props.editingNotificationPermissionLabelsId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (this.props.editingNotificationPermissionLabelsId) {
      const setting = this.props.commonSettings.notificationPermissionLabelSettings?.find(
        (x) => x.id === this.props.editingNotificationPermissionLabelsId
      );

      this.setState({ editingNotificationPermissionLabelsSetting: setting }, () => {
        if (setting) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          this.formRef.current?.resetFields();
          fieldsValue.name = setting.name;
          this.formRef.current?.setFieldsValue(fieldsValue);
          this.setState({ notificationPermissionLabels: setting.values });
        }
      });
    } else {
      const values: any[] = [];
      this.props.languages.forEach((language) => {
        values.push({
          languageName: language.shortName,
          cultureName: language.name,
          value: ""
        });
      });
      this.setState({ notificationPermissionLabels: values });
    }
  };

  existingNameValidator = async (_rule, value) => {
    const settings = this.props.commonSettings?.notificationPermissionLabelSettings;

    const isSettingAlreadyExist =
      settings?.some((x) => x.name === value && !this.props.editingNotificationPermissionLabelsId) ||
      value === "Default";

    if (isSettingAlreadyExist) throw new Error("Notification Permission Labels Setting Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();

    let obj: INotificationPermissionLabelSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      values: this.state.notificationPermissionLabels?.map((notification) => ({
        ...notification,
        value: notification.value?.trim()
      }))
    };
    if (this.props.editingNotificationPermissionLabelsId) {
      obj.id = this.props.editingNotificationPermissionLabelsId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "notificationPermissionLabelSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "notificationPermissionLabelSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingNotificationPermissionLabelsId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    const parentDataKey = this.props.formInputName[0] === "androidConfig" ? "iosConfig" : "androidConfig";

    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingNotificationPermissionLabelsId || "",
      configKey: "notificationPermissionLabelSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Notification Permission Labels setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      shouldControlOtherSections: {
        listOfControl: [[parentDataKey, this.props.formInputName[1]]],
        onCannotDelete: () => {
          KMainFunctions.displayErrorNotification(`
          This Notification Permission Labels setting is
          used on ${
            this.props.formInputName[0] === "androidConfig" ? "IOS" : "Android"
          } section. Please discard this setting
          on other config section before delete.
        `);
        }
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  getSectionDescription = (): React.ReactNode => {
    const contentKey =
      this.props.formInputName[0] === "androidConfig"
        ? "notificationpermissionlabelssettingspopup_notificationpermissionlabelssettings_description_android"
        : "notificationpermissionlabelssettingspopup_notificationpermissionlabelssettings_description_ios";

    return this.props.getSectionDescription(contentKey);
  };

  setValue = (e, lang) => {
    const notificationPermissionLabels = _.cloneDeep(this.state.notificationPermissionLabels);
    notificationPermissionLabels?.forEach((value) => {
      if (value.cultureName === lang.cultureName) {
        value.value = e.target.value;
      }
    });
    this.setState({ notificationPermissionLabels });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Notification Permission Labels Setting"
        secondHeader="Notification Permission Labels Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingNotificationPermissionLabelsId ? this.deleteSetting : undefined}
            deleteTestId="pushNotificationPermissionTextModalDeleteButton"
            submitTestId={`pushNotificationPermissionTextModal${
              this.props.editingNotificationPermissionLabelsId ? "Update" : "Create"
            }Button`}
            submitLabel={this.props.editingNotificationPermissionLabelsId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configNotificationPermissionLabelsForm"
          />
        }
      >
        <Form
          id="configNotificationPermissionLabelsForm"
          name="notificationPermissionLabelsSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
        >
          <ConfigModalBody description={this.getSectionDescription()}>
            <div>
              <ConfigField
                label="Name"
                required
                tooltipContentKey="notificationpermissionlabelssettingspopup_notificationpermissionlabelssettings_name"
              >
                <Form.Item
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: "Please enter a Notification Permission Labels Setting Name."
                    },
                    {
                      pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                      message: "Notification Permission Labels Setting Name is invalid"
                    },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="pushNotificationPermissionTextModalNameInput" autoFocus />
                </Form.Item>
              </ConfigField>
              {this.state.notificationPermissionLabels?.map((value) => {
                return (
                  <ConfigField
                    key={value.cultureName}
                    label={value.languageName}
                    tooltipContentKey="notificationpermissionlabelssettingspopup_notificationpermissionlabelssettings_english"
                  >
                    <div style={{ width: 380, marginBottom: 24 }}>
                      <KuikaInput
                        size="md"
                        data-testid="pushNotificationPermissionTextModalEnglishInput"
                        value={value.value}
                        onChange={(e) => this.setValue(e, value)}
                      />
                    </div>
                  </ConfigField>
                );
              })}
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
